import { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Search, MapPin, ExternalLink, GraduationCap, Users, Calendar, Scale, Stethoscope, BookOpen } from 'lucide-react';

interface HBCU {
  id: string;
  name: string;
  city: string;
  state: string;
  stateFullName: string;
  type: 'Public' | 'Private';
  founded: number;
  website: string;
  description: string;
  latitude: number;
  longitude: number;
  enrollment: number;
}

interface ProfSchool {
  id: string;
  name: string;
  city: string;
  state: string;
  stateFullName: string;
  type: 'Public' | 'Private';
  schoolType: 'Law' | 'Medical' | 'Dental' | 'Pharmacy';
  founded: number;
  accreditation: string;
  degree: string;
  website: string;
  description: string;
  enrollment: number;
  notable: string;
}

type Tab = 'hbcu' | 'law' | 'medical';

const STATE_NAMES: Record<string, string> = {
  AL: 'Alabama', AR: 'Arkansas', CA: 'California', DC: 'D.C.', DE: 'Delaware',
  FL: 'Florida', GA: 'Georgia', KY: 'Kentucky', LA: 'Louisiana',
  MD: 'Maryland', MI: 'Michigan', MO: 'Missouri', MS: 'Mississippi',
  NC: 'North Carolina', OH: 'Ohio', OK: 'Oklahoma', PA: 'Pennsylvania',
  SC: 'South Carolina', TN: 'Tennessee', TX: 'Texas', VA: 'Virginia',
  WV: 'West Virginia',
};

const SCHOOL_TYPE_COLORS: Record<string, string> = {
  Law:      'bg-red-900/50 text-red-300 border-red-700/50',
  Medical:  'bg-blue-900/50 text-blue-300 border-blue-700/50',
  Dental:   'bg-teal-900/50 text-teal-300 border-teal-700/50',
  Pharmacy: 'bg-green-900/50 text-green-300 border-green-700/50',
};

const DEGREE_COLORS: Record<string, string> = {
  'JD':      'bg-red-800/60 text-red-200',
  'MD':      'bg-blue-800/60 text-blue-200',
  'DDS':     'bg-teal-800/60 text-teal-200',
  'PharmD':  'bg-green-800/60 text-green-200',
};

export default function HBCUPage() {
  const [activeTab, setActiveTab] = useState<Tab>('hbcu');
  const [search, setSearch] = useState('');
  const [selectedState, setSelectedState] = useState('ALL');
  const [selectedType, setSelectedType] = useState<'All' | 'Public' | 'Private'>('All');

  const [hbcus, setHbcus] = useState<HBCU[]>([]);
  const [profSchools, setProfSchools] = useState<ProfSchool[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const loadData = async () => {
    if (loaded) return;
    setLoading(true);
    try {
      const [hbcuRes, profRes] = await Promise.all([
        fetch('/api/hbcus'),
        fetch('/api/professional-schools'),
      ]);
      const [hbcuData, profData] = await Promise.all([hbcuRes.json(), profRes.json()]);
      setHbcus(hbcuData);
      setProfSchools(profData);
      setLoaded(true);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  useState(() => { loadData(); });

  // ── Filtered lists ──────────────────────────────────────────────────────────
  const filteredHBCUs = useMemo(() => hbcus.filter(h => {
    const q = search.toLowerCase();
    const matchesSearch = !q || h.name.toLowerCase().includes(q) ||
      h.city.toLowerCase().includes(q) || h.stateFullName.toLowerCase().includes(q);
    const matchesState = selectedState === 'ALL' || h.state === selectedState;
    const matchesType  = selectedType === 'All' || h.type === selectedType;
    return matchesSearch && matchesState && matchesType;
  }), [hbcus, search, selectedState, selectedType]);

  const lawSchools = useMemo(() => profSchools.filter(s =>
    s.schoolType === 'Law' &&
    (!search || s.name.toLowerCase().includes(search.toLowerCase()) ||
     s.city.toLowerCase().includes(search.toLowerCase())) &&
    (selectedState === 'ALL' || s.state === selectedState) &&
    (selectedType === 'All' || s.type === selectedType)
  ), [profSchools, search, selectedState, selectedType]);

  const medSchools = useMemo(() => profSchools.filter(s =>
    (s.schoolType === 'Medical' || s.schoolType === 'Dental' || s.schoolType === 'Pharmacy') &&
    (!search || s.name.toLowerCase().includes(search.toLowerCase()) ||
     s.city.toLowerCase().includes(search.toLowerCase())) &&
    (selectedState === 'ALL' || s.state === selectedState) &&
    (selectedType === 'All' || s.type === selectedType)
  ), [profSchools, search, selectedState, selectedType]);

  // ── Stats ───────────────────────────────────────────────────────────────────
  const hbcuStats = useMemo(() => ({
    total: hbcus.length,
    public: hbcus.filter(h => h.type === 'Public').length,
    private: hbcus.filter(h => h.type === 'Private').length,
    states: new Set(hbcus.map(h => h.state)).size,
  }), [hbcus]);

  const allHBCUStates = useMemo(() => [...new Set(hbcus.map(h => h.state))].sort(), [hbcus]);
  const allProfStates  = useMemo(() => [...new Set(profSchools.map(s => s.state))].sort(), [profSchools]);

  const stateOptions = activeTab === 'hbcu' ? allHBCUStates : allProfStates;
  const displayCount = activeTab === 'hbcu' ? filteredHBCUs.length
    : activeTab === 'law' ? lawSchools.length : medSchools.length;

  // ── Tab config ──────────────────────────────────────────────────────────────
  const tabs: { key: Tab; label: string; icon: React.ReactNode; count: number; accent: string }[] = [
    { key: 'hbcu',    label: 'HBCUs',           icon: <GraduationCap className="h-4 w-4" />, count: hbcus.length,                                              accent: 'border-purple-500 text-purple-300' },
    { key: 'law',     label: 'Law Schools',      icon: <Scale className="h-4 w-4" />,         count: profSchools.filter(s => s.schoolType === 'Law').length,     accent: 'border-red-500 text-red-300' },
    { key: 'medical', label: 'Health Schools',   icon: <Stethoscope className="h-4 w-4" />,   count: profSchools.filter(s => s.schoolType !== 'Law').length,     accent: 'border-blue-500 text-blue-300' },
  ];

  const activeTabConfig = tabs.find(t => t.key === activeTab)!;

  return (
    <div className="min-h-screen bg-gray-950">
      <Helmet>
        <title>HBCU &amp; Professional Schools | BLK BOOK</title>
        <meta name="description" content="Explore HBCUs, HBCU law schools, and historically Black medical, dental, and pharmacy schools." />
      </Helmet>

      {/* Hero */}
      <div className="relative bg-gradient-to-br from-gray-950 via-purple-950 to-gray-950 py-14 px-4">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-purple-900/30 via-transparent to-transparent" />
        <div className="relative max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-purple-900/50 border border-purple-700/50 text-purple-300 text-sm px-4 py-1.5 rounded-full mb-5">
            <BookOpen className="h-4 w-4" />
            Black Excellence in Education
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">
            HBCUs &amp; Professional Schools
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-8">
            Every historically Black college, law school, medical school, dental school, and pharmacy program — in one place.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl mx-auto">
            {[
              { label: 'HBCUs',         value: hbcuStats.total },
              { label: 'Law Schools',   value: profSchools.filter(s => s.schoolType === 'Law').length },
              { label: 'Med & Dental',  value: profSchools.filter(s => ['Medical','Dental'].includes(s.schoolType)).length },
              { label: 'Pharmacy',      value: profSchools.filter(s => s.schoolType === 'Pharmacy').length },
            ].map(stat => (
              <div key={stat.label} className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-3">
                <div className="text-2xl font-bold text-white">{stat.value || '—'}</div>
                <div className="text-xs text-gray-400 uppercase tracking-wide">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-b border-gray-800 bg-gray-950 px-4">
        <div className="max-w-6xl mx-auto flex gap-1">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setSearch(''); setSelectedState('ALL'); setSelectedType('All'); }}
              className={`flex items-center gap-2 px-5 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? `${tab.accent} bg-white/5`
                  : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-600'
              }`}
            >
              {tab.icon}
              {tab.label}
              {tab.count > 0 && (
                <span className="text-xs bg-white/10 rounded-full px-2 py-0.5">{tab.count}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="sticky top-0 z-10 bg-gray-950/95 backdrop-blur border-b border-gray-800 px-4 py-3">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <input
              type="text"
              placeholder={`Search ${activeTabConfig.label.toLowerCase()}...`}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 text-sm"
            />
          </div>

          <select
            value={selectedState}
            onChange={e => setSelectedState(e.target.value)}
            className="px-3 py-2.5 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500"
          >
            <option value="ALL">All States</option>
            {stateOptions.map(s => (
              <option key={s} value={s}>{STATE_NAMES[s] || s}</option>
            ))}
          </select>

          <div className="flex gap-2">
            {(['All', 'Public', 'Private'] as const).map(t => (
              <button
                key={t}
                onClick={() => setSelectedType(t)}
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  selectedType === t
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-900 border border-gray-700 text-gray-400 hover:text-white'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="max-w-6xl mx-auto mt-1.5">
          <p className="text-xs text-gray-500">Showing {displayCount} {activeTabConfig.label.toLowerCase()}</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500" />
            <p className="text-gray-400">Loading...</p>
          </div>
        ) : activeTab === 'hbcu' ? (
          <HBCUGrid hbcus={filteredHBCUs} />
        ) : activeTab === 'law' ? (
          <ProfSchoolGrid schools={lawSchools} emptyLabel="law schools" />
        ) : (
          <ProfSchoolGrid schools={medSchools} emptyLabel="health professional schools" />
        )}
      </div>
    </div>
  );
}

// ── HBCU Grid ──────────────────────────────────────────────────────────────────
function HBCUGrid({ hbcus }: { hbcus: HBCU[] }) {
  const allStates = [...new Set(hbcus.map(h => h.state))].sort();

  if (hbcus.length === 0) {
    return (
      <div className="text-center py-20">
        <GraduationCap className="mx-auto h-12 w-12 text-gray-600 mb-4" />
        <h3 className="text-lg font-medium text-white mb-2">No results found</h3>
        <p className="text-gray-500">Try adjusting your search or filters.</p>
      </div>
    );
  }

  return (
    <div className="space-y-10">
      {allStates.map(state => {
        const stateHBCUs = hbcus.filter(h => h.state === state);
        return (
          <div key={state}>
            <div className="flex items-center gap-3 mb-4">
              <h2 className="text-lg font-bold text-white">
                {state === 'DC' ? 'Washington, D.C.' : stateHBCUs[0]?.stateFullName || state}
              </h2>
              <span className="text-xs bg-purple-900/50 border border-purple-700/50 text-purple-300 px-2 py-0.5 rounded-full">
                {stateHBCUs.length} {stateHBCUs.length === 1 ? 'institution' : 'institutions'}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {stateHBCUs.map(hbcu => (
                <HBCUCard key={hbcu.id} hbcu={hbcu} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function HBCUCard({ hbcu }: { hbcu: HBCU }) {
  return (
    <div className="group bg-gray-900 border border-gray-800 rounded-xl p-5 hover:border-purple-700/60 transition-all duration-200 flex flex-col">
      <div className="flex items-start justify-between mb-3">
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${
          hbcu.type === 'Public'
            ? 'bg-blue-950/50 text-red-400 border-red-800/50'
            : 'bg-blue-900/50 text-blue-400 border-blue-800/50'
        }`}>
          {hbcu.type}
        </span>
        <span className="text-xs text-gray-500 flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          Est. {hbcu.founded}
        </span>
      </div>
      <h3 className="text-base font-semibold text-white mb-1 group-hover:text-purple-300 transition-colors leading-tight">
        {hbcu.name}
      </h3>
      <div className="flex items-center gap-1.5 text-sm text-gray-400 mb-3">
        <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
        <span>{hbcu.city}, {hbcu.state}</span>
      </div>
      <p className="text-xs text-gray-500 leading-relaxed mb-4 flex-1 line-clamp-3">
        {hbcu.description}
      </p>
      <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-800">
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <Users className="h-3.5 w-3.5" />
          <span>{hbcu.enrollment?.toLocaleString()} enrolled</span>
        </div>
        <a
          href={hbcu.website}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-xs text-purple-400 hover:text-purple-300 transition-colors font-medium"
          onClick={e => e.stopPropagation()}
        >
          Visit <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </div>
  );
}

// ── Professional School Grid ────────────────────────────────────────────────────
function ProfSchoolGrid({ schools, emptyLabel }: { schools: ProfSchool[]; emptyLabel: string }) {
  if (schools.length === 0) {
    return (
      <div className="text-center py-20">
        <GraduationCap className="mx-auto h-12 w-12 text-gray-600 mb-4" />
        <h3 className="text-lg font-medium text-white mb-2">No results found</h3>
        <p className="text-gray-500">Try adjusting your search or filters.</p>
      </div>
    );
  }

  // Group by schoolType
  const groups: Record<string, ProfSchool[]> = {};
  schools.forEach(s => {
    if (!groups[s.schoolType]) groups[s.schoolType] = [];
    groups[s.schoolType].push(s);
  });
  const typeOrder = ['Law', 'Medical', 'Dental', 'Pharmacy'];
  const orderedGroups = typeOrder.filter(t => groups[t]?.length).map(t => ({ type: t, schools: groups[t] }));

  return (
    <div className="space-y-10">
      {orderedGroups.map(({ type, schools: groupSchools }) => (
        <div key={type}>
          <div className="flex items-center gap-3 mb-5">
            <h2 className="text-lg font-bold text-white">
              {type === 'Medical' ? 'Medical Schools (MD)' :
               type === 'Dental'  ? 'Dental Schools (DDS)' :
               type === 'Pharmacy' ? 'Pharmacy Schools (PharmD)' :
               'Law Schools (JD)'}
            </h2>
            <span className={`text-xs px-2 py-0.5 rounded-full border ${SCHOOL_TYPE_COLORS[type]}`}>
              {groupSchools.length} {groupSchools.length === 1 ? 'school' : 'schools'}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {groupSchools.map(school => (
              <ProfSchoolCard key={school.id} school={school} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ProfSchoolCard({ school }: { school: ProfSchool }) {
  const accentClass = SCHOOL_TYPE_COLORS[school.schoolType] || 'bg-gray-800 text-gray-300 border-gray-600';
  const degreeClass = DEGREE_COLORS[school.degree] || 'bg-gray-700 text-gray-300';

  return (
    <div className="group bg-gray-900 border border-gray-800 rounded-xl p-5 hover:border-purple-700/60 transition-all duration-200 flex flex-col">
      {/* Type + degree badges */}
      <div className="flex items-start justify-between mb-3 gap-2 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${accentClass}`}>
            {school.schoolType}
          </span>
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${degreeClass}`}>
            {school.degree}
          </span>
        </div>
        <span className="text-xs text-gray-500 flex items-center gap-1 flex-shrink-0">
          <Calendar className="h-3 w-3" />
          Est. {school.founded}
        </span>
      </div>

      {/* Name */}
      <h3 className="text-base font-semibold text-white mb-1 group-hover:text-purple-300 transition-colors leading-tight">
        {school.name}
      </h3>

      {/* Location + accreditation */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5 text-sm text-gray-400">
          <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
          <span>{school.city}, {school.state}</span>
        </div>
        <span className="text-xs text-gray-600 font-mono">{school.accreditation}</span>
      </div>

      {/* Description */}
      <p className="text-xs text-gray-500 leading-relaxed mb-3 flex-1 line-clamp-4">
        {school.description}
      </p>

      {/* Notable alumni */}
      {school.notable && (
        <div className="mb-3 px-3 py-2 bg-white/5 rounded-lg border border-white/10">
          <p className="text-xs text-gray-400">
            <span className="text-gray-300 font-medium">Notable: </span>
            {school.notable}
          </p>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-800">
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <Users className="h-3.5 w-3.5" />
          <span>{school.enrollment?.toLocaleString()} enrolled</span>
        </div>
        <a
          href={school.website}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-xs text-purple-400 hover:text-purple-300 transition-colors font-medium"
          onClick={e => e.stopPropagation()}
        >
          Visit <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </div>
  );
}
